import { normalizeHexColor } from './defeitoAlertBadge';

export function formatSeverityLabel(raw) {
  const t = String(raw ?? '').trim();
  if (!t) return '';
  return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
}

export function contrastTextOnHex(hex) {
  const h = normalizeHexColor(hex);
  if (!h) return '#1e293b';
  const r = parseInt(h.slice(1, 3), 16);
  const g = parseInt(h.slice(3, 5), 16);
  const b = parseInt(h.slice(5, 7), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.55 ? '#0f172a' : '#ffffff';
}

export function formatDateBr(value, withTime = false) {
  if (value == null || value === '') return '—';
  const s = String(value).trim();
  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) {
    const base = `${ymd[3]}/${ymd[2]}/${ymd[1]}`;
    if (!withTime) return base;
    const time = s.match(/(\d{2}):(\d{2})/);
    return time ? `${base} ${time[1]}:${time[2]}` : base;
  }
  const dmy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (dmy) {
    const base = `${dmy[1].padStart(2, '0')}/${dmy[2].padStart(2, '0')}/${dmy[3]}`;
    if (!withTime) return base;
    const time = s.match(/(\d{2}):(\d{2})/);
    return time ? `${base} ${time[1]}:${time[2]}` : base;
  }
  const d = value instanceof Date ? value : new Date(s);
  if (!Number.isNaN(d.getTime())) {
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const base = `${dd}/${mm}/${yyyy}`;
    if (!withTime) return base;
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${base} ${hh}:${mi}`;
  }
  return s || '—';
}

export function formatPhoneDisplay(value) {
  const n = (value || '').replace(/\D/g, '');
  if (!n.length) return '';
  if (n.length <= 2) return `(${n}`;
  if (n.length <= 6) return `(${n.slice(0, 2)})${n.slice(2)}`;
  if (n.length <= 10) return `(${n.slice(0, 2)})${n.slice(2, 6)}-${n.slice(6, 10)}`;
  return `(${n.slice(0, 2)})${n.slice(2, 7)}-${n.slice(7, 11)}`;
}

export const OS_TIPO_PREVENTIVA_OPTIONS = [
  { code: 'A', label: 'Aferição' },
  { code: 'B', label: 'Balcão' },
  { code: 'D', label: 'Desinstalação' },
  { code: 'N', label: 'Corretiva' },
  { code: 'E', label: 'Estoque' },
  { code: 'I', label: 'Instalação' },
  { code: 'S', label: 'Preventiva' },
];

const OS_TIPO_LABEL_BY_CODE = Object.fromEntries(OS_TIPO_PREVENTIVA_OPTIONS.map((o) => [o.code, o.label]));

/** Código TB02115_PREVENTIVA normalizado (C legado → N). */
export function getOrderTipoPreventivaCode(order) {
  const raw = String(order?.TB02115_PREVENTIVA ?? order?.tipo ?? '')
    .trim()
    .toUpperCase();
  if (raw === 'C') return 'N';
  return raw;
}

export function getOsTipoChangeOptions(currentCode) {
  const cur = String(currentCode ?? '')
    .trim()
    .toUpperCase();
  const normalized = cur === 'C' ? 'N' : cur;
  return OS_TIPO_PREVENTIVA_OPTIONS.filter((o) => o.code !== normalized);
}

export function getServiceTypeName(tipo) {
  const code = String(tipo ?? '')
    .trim()
    .toUpperCase();
  if (code === 'C') return OS_TIPO_LABEL_BY_CODE.N;
  if (OS_TIPO_LABEL_BY_CODE[code]) return OS_TIPO_LABEL_BY_CODE[code];
  const types = {
    R: 'Retorno/Recarga',
  };
  return types[code] || 'Corretiva';
}

export function getSLAStatus(sla) {
  const status = {
    Vencido: { text: 'Vencido', tone: 'vencido' },
    vencido: { text: 'Vencido', tone: 'vencido' },
    Vencendo: { text: 'Vencendo', tone: 'vencendo' },
    vencendo: { text: 'Vencendo', tone: 'vencendo' },
    'Em dia': { text: 'Em dia', tone: 'ok' },
    ok: { text: 'Em dia', tone: 'ok' },
  };
  return status[sla] || { text: 'Em dia', tone: 'ok' };
}

function parseBrDateTime(value) {
  if (value == null || value === '') return null;
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const parts = s.split(/\s+/);
  const datePart = parts[0] || '';
  const timePart = parts[1] || '00:00';
  const [day, month, year] = datePart.split('/').map(Number);
  const [hour, min] = timePart.split(':').map(Number);
  if (!day || !month || !year) return null;
  const d = new Date(year, month - 1, day, hour || 0, min || 0, 0, 0);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Progresso do SLA entre abertura e previsão (0–100). */
export function getSlaTimeProgress(order) {
  const start =
    parseBrDateTime(order?.dataAbertura) ??
    parseBrDateTime(order?.TB02115_DATA);
  const end =
    parseBrDateTime(order?.previsao) ??
    parseBrDateTime(order?.CALC_PREVISAO);
  if (!start || !end) return null;
  const totalMs = end.getTime() - start.getTime();
  if (totalMs <= 0) return null;
  const elapsedMs = Date.now() - start.getTime();
  const percent = Math.min(100, Math.max(0, (elapsedMs / totalMs) * 100));
  return { percent, isOverdue: elapsedMs >= totalMs };
}

export function buildSlaStatusMessage(order, slaInfo) {
  const abertura = String(order?.dataAbertura ?? '').trim() || '—';
  const previsao = String(order?.previsao ?? '').trim() || '—';
  let prefix = slaInfo.text;
  if (slaInfo.tone === 'vencido') {
    const dueDate =
      parseBrDateTime(order?.previsao) ?? parseBrDateTime(order?.CALC_PREVISAO);
    if (dueDate) {
      const diffMs = dueDate.getTime() - Date.now();
      if (diffMs < 0) {
        const totalMin = Math.floor(Math.abs(diffMs) / 60000);
        const days = Math.floor(totalMin / (24 * 60));
        if (days > 0) prefix = `Vencido há ${days} dia${days === 1 ? '' : 's'}`;
      }
    }
  }
  return `${prefix} (Abertura: ${abertura} - Previsão: ${previsao})`;
}

export function collectOrderPhones(order) {
  const list = [];
  const push = (raw, label) => {
    const v = String(raw ?? '').trim();
    if (v) list.push({ label, display: formatPhoneDisplay(v), raw: v });
  };
  push(order?.TB02115_FONE, 'Fone');
  push(order?.TB02115_FONE2, 'Fone 2');
  push(order?.TB02115_CELULAR, 'Celular');
  return list;
}

/** Data/hora status pedido (TB02130_DATA) em dd/MM/yyyy HH:mm, sem deslocar fuso (igual OrderSidebar). */
export function formatPedidoTimelineDateBr(dateString) {
  if (!dateString) return '—';
  try {
    if (dateString instanceof Date && !Number.isNaN(dateString.getTime())) {
      const isoString = dateString.toISOString();
      const match = isoString.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
      if (match) {
        const [, year, month, day, hours, minutes] = match;
        return `${day}/${month}/${year} ${hours}:${minutes}`;
      }
      const year = dateString.getUTCFullYear();
      const month = String(dateString.getUTCMonth() + 1).padStart(2, '0');
      const day = String(dateString.getUTCDate()).padStart(2, '0');
      const hours = String(dateString.getUTCHours()).padStart(2, '0');
      const minutes = String(dateString.getUTCMinutes()).padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    }
    let dateStr = String(dateString).trim();
    dateStr = dateStr.replace(/T/g, ' ').replace(/Z$/, '').replace(/[+-]\d{2}:\d{2}$/, '');
    const match = dateStr.match(/(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?/);
    if (match) {
      const [, year, month, day, hours, minutes] = match;
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    }
    const matchShort = dateStr.match(/(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})/);
    if (matchShort) {
      const [, year, month, day, hours, minutes] = matchShort;
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    }
    return dateStr;
  } catch {
    return '—';
  }
}
